import { useState } from "react";
import { MapPin, Briefcase, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import CTASection from "@/components/CTASection";
import CareerApplyModal from "@/components/CareerApplyModal";
import { CAREERS, COMPANY } from "@/data/content";
import { toast } from "sonner";

export default function Careers() {
  const [selected, setSelected] = useState(null);

  return (
    <>
      <SEO title="Careers" description="Join DataMeridian Consulting — a global practice of senior database engineers, cloud architects and data engineers." />

      <section className="bg-[var(--dm-navy)] text-white">
        <div className="dm-container py-24 lg:py-32">
          <div className="dm-overline text-[var(--dm-emerald)]">Careers</div>
          <h1 className="font-serif text-5xl sm:text-6xl mt-6 max-w-4xl leading-[1.05]">Work with senior specialists who still love the platform.</h1>
          <p className="text-white/70 text-lg mt-8 max-w-2xl">We hire experienced database, cloud and data engineers who want to work on genuinely hard problems, with a team that has each other's back.</p>
        </div>
      </section>

      <section className="bg-white">
        <div className="dm-container dm-section">
          <div className="dm-overline">Open roles</div>
          <div className="grid gap-px bg-[var(--dm-border)] mt-6 border border-[var(--dm-border)]">
            {CAREERS.map((r) => (
              <div key={r.slug} data-testid={`career-${r.slug}`} className="bg-white p-8 grid md:grid-cols-12 gap-6 items-center hover:bg-[var(--dm-surface)] transition-colors">
                <div className="md:col-span-6">
                  <div className="dm-overline">{r.team}</div>
                  <div className="font-serif text-2xl text-[var(--dm-navy)] mt-2">{r.title}</div>
                </div>
                <div className="md:col-span-4 text-sm text-[var(--dm-muted)] flex flex-col gap-1">
                  <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-[var(--dm-emerald)]" /> {r.location}</span>
                  <span className="inline-flex items-center gap-2"><Briefcase className="w-4 h-4 text-[var(--dm-emerald)]" /> {r.type}</span>
                </div>
                <div className="md:col-span-2 flex md:justify-end">
                  <button
                    onClick={() => setSelected(r)}
                    data-testid={`career-apply-${r.slug}`}
                    className="dm-btn-outline"
                  >
                    Apply <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-14 border border-[var(--dm-border)] p-8 bg-[var(--dm-surface)]">
            <div className="font-serif text-2xl text-[var(--dm-navy)]">Don't see your role?</div>
            <p className="text-[var(--dm-muted)] mt-2">We always want to hear from great engineers. Send us a general application with your resume.</p>
            <button
              data-testid="career-general-apply"
              onClick={() => setSelected({ slug: "general-application", title: "General Application" })}
              className="dm-btn-outline mt-6"
            >
              Send general application <ArrowRight className="w-4 h-4" />
            </button>
            <div className="mt-4 text-xs text-[var(--dm-muted)]">
              Or email us directly at{" "}
              <button
                onClick={() => { navigator.clipboard.writeText(COMPANY.email); toast.success("Email copied to clipboard"); }}
                className="text-[var(--dm-emerald)] font-semibold hover:underline"
              >
                {COMPANY.email}
              </button>
            </div>
          </div>
        </div>
      </section>

      <CTASection eyebrow="Work with us" title="Curious about joining DataMeridian?" body="Apply through any of the roles above and a member of the team will get back to you within one business day." />

      {selected && (
        <CareerApplyModal
          role={selected}
          open={!!selected}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}
